import React, { useMemo } from 'react';
import { DeviceTypeId, getDeviceTypeColor } from '../../../constants/deviceTypes';
import { PLCDeviceCard, CameraDeviceCard, USBDeviceCard, RestDeviceCard } from './cards';
import GroupHeader from './GroupHeader';
import { getLatestMetricValue } from '../hooks/useDeviceHierarchy';

interface Device {
  id: number;
  name: string;
  status: 0 | 1;
  socketConnected?: boolean;
  groupId?: number;
  connectedTo?: number;
  deviceTypes?: {
    id: number;
    name: string;
    icon?: string;
  };
  settings?: {
    cameraStreamEndpoint?: string;
    [key: string]: any;
  };
}

interface DeviceGroup {
  id: number;
  name: string;
  devices?: Device[];
}

interface DashboardGroupCardProps {
  /** Device group (may be null if device has no group) */
  group: DeviceGroup | null;
  /** Host device within this group */
  hostDevice: Device;
  /** All child devices connected to this host */
  childDevices: Device[];
  /** Metrics data keyed by deviceId */
  deviceMetrics: Record<number, Record<string, any[]>>;
  /** Callback when capture button is clicked */
  onCaptureClick?: (cameraDevice: Device) => void;
}

/**
 * Render the appropriate card for a child device based on its type
 * Note: Host devices are rendered in the GroupHeader, not as separate cards
 */
function renderChildDeviceCard(
  device: Device,
  deviceMetrics: Record<number, Record<string, any[]>>,
  themeColor: string,
  onCaptureClick?: (device: Device) => void
): React.ReactNode {
  switch (device.deviceTypes?.id) {
    case DeviceTypeId.PLC:
      return (
        <PLCDeviceCard
          key={device.id}
          device={device}
          deviceMetrics={deviceMetrics}
          themeColor={getDeviceTypeColor(DeviceTypeId.PLC).border}
        />
      );
    case DeviceTypeId.CAMERA:
      return (
        <CameraDeviceCard
          key={device.id}
          device={device}
          themeColor={getDeviceTypeColor(DeviceTypeId.CAMERA).border}
          onCaptureClick={onCaptureClick}
        />
      );
    case DeviceTypeId.USB:
      return (
        <USBDeviceCard
          key={device.id}
          device={device}
          deviceMetrics={deviceMetrics}
          themeColor={getDeviceTypeColor(DeviceTypeId.USB).border}
        />
      );
    case DeviceTypeId.REST:
      return (
        <RestDeviceCard
          key={device.id}
          device={device}
          deviceMetrics={deviceMetrics}
          themeColor={getDeviceTypeColor(DeviceTypeId.REST).border}
        />
      );
    default:
      // Generic device - use USB card as fallback
      return (
        <USBDeviceCard
          key={device.id}
          device={device}
          deviceMetrics={deviceMetrics}
          themeColor="#9e9e9e"
        />
      );
  }
}

/**
 * Container component for a device group on the dashboard
 * Renders the group header (with host metrics) and child device cards
 */
const DashboardGroupCard: React.FC<DashboardGroupCardProps> = ({
  group,
  hostDevice,
  childDevices,
  deviceMetrics,
  onCaptureClick,
}) => {
  // Get theme color from device type
  const themeColor = getDeviceTypeColor(hostDevice.deviceTypes?.id || DeviceTypeId.HOST).border;

  // Get group name (fallback to host device name)
  const groupName = group?.name || hostDevice.name || `Host ${hostDevice.id}`;

  // Count online devices (using socketConnected for live status)
  const onlineCount = useMemo(() => {
    let count = hostDevice.socketConnected ? 1 : 0;
    childDevices.forEach(d => {
      if (d.socketConnected) count++;
    });
    return count;
  }, [hostDevice, childDevices]);

  // Extract all host metrics for the header
  const hostMetrics = useMemo(() => {
    const cpuPercent = getLatestMetricValue(deviceMetrics, hostDevice.id, 'cpu_usage_percent') ?? 0;
    const ramUsedMB = getLatestMetricValue(deviceMetrics, hostDevice.id, 'ram_used_mb') ?? 0;
    const ramTotalMB = getLatestMetricValue(deviceMetrics, hostDevice.id, 'ram_total_mb') ?? 1;
    const ramPercent = ramTotalMB > 0 ? (ramUsedMB / ramTotalMB) * 100 : 0;
    const ramUsedGB = ramUsedMB / 1024;
    const ramTotalGB = ramTotalMB / 1024;
    const cpuTemp = getLatestMetricValue(deviceMetrics, hostDevice.id, 'cpu_temp') ?? 0;
    const gpuTemp = getLatestMetricValue(deviceMetrics, hostDevice.id, 'gpu_temp') ?? 0;
    const diskUsedGB = getLatestMetricValue(deviceMetrics, hostDevice.id, 'disk_used_gb') ?? 0;
    const diskTotalGB = getLatestMetricValue(deviceMetrics, hostDevice.id, 'disk_total_gb') ?? 0;
    const diskPercent = getLatestMetricValue(deviceMetrics, hostDevice.id, 'disk_usage_percent') ?? 0;
    const powerCurrentMW = getLatestMetricValue(deviceMetrics, hostDevice.id, 'power_current_mw') ?? 0;
    const powerWatts = powerCurrentMW / 1000;

    return {
      cpuPercent,
      ramPercent,
      ramUsedGB,
      ramTotalGB,
      cpuTemp,
      gpuTemp,
      diskPercent,
      diskUsedGB,
      diskTotalGB,
      powerWatts,
    };
  }, [deviceMetrics, hostDevice.id]);

  // Get icon for host device type
  const icon = hostDevice.deviceTypes?.icon || '🖥️';

  return (
    <div style={{ marginBottom: '2rem' }}>
      {/* Group Header with host metrics */}
      <GroupHeader
        groupName={groupName}
        hostName={hostDevice.name}
        hostDevice={hostDevice}
        hostMetrics={hostMetrics}
        activeDevicesCount={onlineCount}
        themeColor={themeColor}
        icon={icon}
      />

      {/* Child Device Cards Grid */}
      {childDevices.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1rem',
        }}>
          {childDevices.map(device =>
            renderChildDeviceCard(device, deviceMetrics, themeColor, onCaptureClick)
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardGroupCard;
export type { DashboardGroupCardProps };
